import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFormatter } from '../../locale/formatting';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { toLatinNumberingSystemLocale } from '../../locale/localeUtils';
import { renderDesignTokens, useDesignTokens } from '../../theme/theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ButtonVariant, getButtonDesignTokens } from '../Button';
import { useDensity } from '../Density';
import { checkValidNumberFormat, getNumberSeparators, parseAndFormatInput, validateNumericInput, validateRangeInput } from '../Input';
import { getInputDesignTokens } from '../Input/__internal__/BaseInput/BaseInput';
import { NumericStepperInput, SpinButtons } from './__internal__';
import './NumericStepper.css';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_MINIMUM_FRACTION_DIGITS = 0;
const DEFAULT_MAXIMUM_FRACTION_DIGITS = 3;

/**
 * The minimum value allowed.
 */
export const MINIMUM_VALUE = -999999999;
/**
 * The maximum value allowed.
 */
export const MAXIMUM_VALUE = 999999999;
/**
 * Minimum number of decimal points.
 */
export const MINIMUM_FRACTION_DIGITS = 0;
// if the value is 999_999_999, there are only 7 decimal places can be valid.
/**
 * Maximum number of decimal points.
 */
export const MAXIMUM_FRACTION_DIGITS = 7;
export const NumericStepper = /*#__PURE__*/forwardRef(({
  allowIntegerOnly = false,
  enableRevertToCommitted = false,
  disabled = false,
  editMode = 'select',
  readOnly = false,
  required = false,
  maximum = MAXIMUM_VALUE,
  minimum = MINIMUM_VALUE,
  stepSize = 1,
  pageSize = stepSize * 2,
  // it can't be set to minimum here when it is undefined,
  // because consumer can't get informed for the change.
  value /* = minimum */,
  status: customizedStatus,
  message: customizedMessage,
  onChange,
  onCommit,
  className,
  tooltip,
  testId,
  ...props
}, ref) => {
  const numberFormatOptions = useMemo(() => {
    const options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    if (!allowIntegerOnly) {
      options.minimumFractionDigits = props.numberFormatOptions?.minimumFractionDigits ?? DEFAULT_MINIMUM_FRACTION_DIGITS;
      options.maximumFractionDigits = props.numberFormatOptions?.maximumFractionDigits ?? Math.max(DEFAULT_MAXIMUM_FRACTION_DIGITS, options.minimumFractionDigits);
    }
    return options;
  }, [allowIntegerOnly, props.numberFormatOptions?.maximumFractionDigits, props.numberFormatOptions?.minimumFractionDigits]);
  assertNumberFormatOptionsValidity(numberFormatOptions);
  assertMinMaxValidity(minimum, maximum, numberFormatOptions.maximumFractionDigits);
  assertStepSizeValidity(stepSize, pageSize, numberFormatOptions.maximumFractionDigits);
  value = typeof value === 'number' ? normalize(value, numberFormatOptions) : value;
  const inputRef = useRef(null);
  // think of this state should not rerender the ui, then useRef instead of useState.
  const isLiveRef = useRef(false);

  // references for commit value
  const lastCommitInfoRef = useRef();
  // when enable revert to commit, and an invalid value detected, this value will be emitted.
  const lastSuccessCommitValueRef = useRef(minimum);
  // the display value in last onChange event
  const lastChangedValueRef = useRef();
  // indicate if a blank value is from user set directly (unexpected), not from onChange event (expected).
  const isUnexpectedBlankValueRef = useRef(false);
  const locale = useFormatLocale();
  const [valueState, setValueState] = useState({
    status: undefined,
    message: ''
  });
  const maxLength = useMemo(() => {
    const options = {
      minimumFractionDigits: numberFormatOptions.maximumFractionDigits,
      maximumFractionDigits: numberFormatOptions.maximumFractionDigits
    };
    return Math.max(parseAndFormatInput(minimum.toString(), undefined, options).formatValue.length, parseAndFormatInput(maximum.toString(), undefined, options).formatValue.length);
  }, [minimum, maximum, numberFormatOptions.maximumFractionDigits]);
  const fireValueChanged = useCallback((displayValue, roundedRawValue, reason) => {
    if (lastChangedValueRef.current !== displayValue) {
      lastChangedValueRef.current = displayValue;
      onChange({
        displayValue,
        numericValue: roundedRawValue,
        reason
      });
    }
  }, [onChange]);
  const fireValueCommitted = useCallback((value, reason) => {
    const errorType = value === null && lastChangedValueRef.current !== undefined ? getErrorType({
      invalidValue: lastChangedValueRef.current,
      min: minimum,
      max: maximum,
      allowIntegerOnly,
      locale
    }) : undefined;
    if (lastCommitInfoRef.current === undefined || lastCommitInfoRef.current.value !== value || lastCommitInfoRef.current.reason !== reason || lastCommitInfoRef.current.error !== errorType) {
      lastCommitInfoRef.current = {
        value,
        reason,
        error: errorType
      };
      if (!isNullish(value)) {
        lastSuccessCommitValueRef.current = value;
      }
      onCommit?.({
        value,
        reason,
        error: errorType
      });
    }
  }, [allowIntegerOnly, locale, maximum, minimum, onCommit]);
  const onSpinButtonsValueChange = e => {
    const {
      formatValue,
      roundedRawValue
    } = parseValue(e.newValue, locale, numberFormatOptions);
    fireValueChanged(formatValue, roundedRawValue, 'press');
  };
  const onSpinButtonsValueCommit = e => {
    isLiveRef.current = false;
    if (e.reason === 'blur') {
      // This is requested in NOVACOMMONS-4457
      fireValueCommitted(isNaN(roundedRawValue) ? null : roundedRawValue, e.reason);
    } else {
      fireValueCommitted(e.value, e.reason);
    }
  };
  const earconInputFull = useEarcon({
    source: EarconName.InputFull
  });
  const earconError = useEarcon({
    source: EarconName.Error
  });
  const formatter = useFormatter();
  const onInputStateChange = e => {
    const {
      rawValue,
      formatValue,
      roundedRawValue
    } = parseValue(e.value, locale, numberFormatOptions);
    switch (e.state) {
      case 'change':
        isLiveRef.current = true;
        const validDecimalValue = getValidDecimalValue(e.value, locale, numberFormatOptions);
        const hasMoreDecimal = validDecimalValue !== e.value;
        if (hasMoreDecimal) {
          earconInputFull.playFromStart();
          const rawValidDecimalValue = parseValue(validDecimalValue, locale, numberFormatOptions).rawValue;
          fireValueChanged(validDecimalValue, rawValidDecimalValue, 'type');
        } else {
          fireValueChanged(e.value, rawValue, 'type');
        }
        break;
      case 'enter':
      case 'blur':
        isLiveRef.current = false;
        const value = e.value;
        const {
          status,
          message
        } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage, formatter);
        if (e.state === 'blur' && (value === '' || status !== undefined) && enableRevertToCommitted) {
          const lastSuccessCommitFormatValue = parseValue(lastSuccessCommitValueRef.current, locale, numberFormatOptions).formatValue;
          // invoke valueChange internally
          fireValueChanged(lastSuccessCommitFormatValue, lastSuccessCommitValueRef.current, 'revise');
          fireValueCommitted(lastSuccessCommitValueRef.current, 'revise');
        } else {
          // onChange
          if (status === undefined && lastChangedValueRef.current !== undefined) {
            // invoke valueChange internally
            fireValueChanged(value === '' ? '' : formatValue, roundedRawValue, e.state);
          } else {
            setValueState({
              status,
              message
            });
          }

          // onCommit
          fireValueCommitted(status === undefined ? value !== '' ? roundedRawValue : undefined : null, e.state);
        }
        break;
      case 'focus':
        isLiveRef.current = true;
        // show raw number to edit in edit mode of 'combo'
        if (editMode === 'combo' && valueState.status === undefined && !isNaN(roundedRawValue) && e.value.toString() !== roundedRawValue.toString()) {
          // invoke ValueChange internally
          fireValueChanged(getLocaleStringValue(roundedRawValue, locale, {
            useGrouping: false,
            ...numberFormatOptions
          }), roundedRawValue, 'focus');
        }
        break;
    }
  };
  const {
    rawValue,
    formatValue,
    roundedRawValue
  } = parseValue(value, locale, numberFormatOptions);
  const formattedMinimum = parseValue(minimum, locale, numberFormatOptions).formatValue;
  useEffect(function enableEarcon() {
    const input = inputRef.current;
    const isInput = input instanceof HTMLInputElement;
    if (!isInput) {
      return;
    }
    const handleKeyDown = e => {
      if (input.disabled || input.readOnly) {
        return;
      }
      if (isPrintableKey(e)) {
        if (input && input.value.length === input.maxLength && (input.selectionEnd ?? 0) - (input.selectionStart ?? 0) === 0) {
          earconInputFull.playFromStart();
          return;
        } else if (isNumberIrrelevantKey(e.key, locale)) {
          earconError.playFromStart();
        }
      }
    };
    input.addEventListener('keydown', handleKeyDown);
    return () => {
      if (!isInput) {
        return;
      }
      input.removeEventListener('keydown', handleKeyDown);
    };
  });
  useEffect(
  // An commit event will be emitted if an invalid value becomes valid due to one of below properties updated:
  // minimum, maximum, customizedStatus, required or allowIntegerOnly.
  function appendCommit() {
    if (isLiveRef.current) return;
    const {
      status
    } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage);
    const errorIsGone = status === undefined && status !== valueState.status;
    const isValidValue = !isNaN(roundedRawValue);
    const valueIsNotCommitted = lastSuccessCommitValueRef.current !== roundedRawValue;
    if (errorIsGone && isValidValue && valueIsNotCommitted) {
      fireValueCommitted(roundedRawValue, 'revise');
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [minimum, maximum, customizedStatus, required, allowIntegerOnly]);
  useEffect(function checkValue() {
    const {
      status,
      message
    } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage);
    const isUntouched = lastCommitInfoRef.current?.value === undefined;
    if (!(value === '' && editMode === 'combo' && required && isUntouched)) {
      setValueState({
        status,
        message
      });
    }
    const isInitial = lastChangedValueRef.current === undefined;
    const isUnexpectedValue = lastChangedValueRef.current !== formatValue && lastChangedValueRef.current !== value;
    isUnexpectedBlankValueRef.current = !isInitial && isUnexpectedValue && value === '';

    // There should be always a value shown in select edit mode.
    // So if a blank string, an undefined or a Non-number value is provided
    // without from onChange event or when initializing,
    // the minimum will be supplied.
    const isInvalidValueToSelectMode = (status || value === '' || value === undefined) && (isInitial || isUnexpectedValue) && editMode === 'select';

    // When initializing a Numeric Stepper in combo edit mode with a undefined value,
    // the minimum value should be shown as well.
    const isInvalidValueToComboMode = value === undefined && isInitial && editMode === 'combo';
    if (isInvalidValueToComboMode || isInvalidValueToSelectMode) {
      // invoke valueChange internally
      fireValueChanged(formattedMinimum, minimum, 'revise');
      fireValueCommitted(minimum, 'revise');
      return;
    }
    if (isInitial && !status) {
      lastSuccessCommitValueRef.current = value === '' ? minimum : rawValue;
    }

    // If a value is changed after formatting, to notice user
    const isCommitted = !isLiveRef.current;
    const isValidValue = !status;
    const hasValue = value !== '';
    const valueIsChangedDueToRound = rawValue !== roundedRawValue;
    if (isCommitted && hasValue && isValidValue && valueIsChangedDueToRound) {
      // invoke valueChange internally to notice user
      fireValueChanged(formatValue, roundedRawValue, 'revise');
      fireValueCommitted(roundedRawValue, 'revise');
      return;
    }

    // reset last commit info and last success commit value if value is provided not from onChange event
    if (!isInitial && isUnexpectedValue) {
      lastChangedValueRef.current = formatValue;
      lastCommitInfoRef.current = undefined;
      lastSuccessCommitValueRef.current = minimum;
    }
  }, [allowIntegerOnly, customizedMessage, customizedStatus, editMode, fireValueChanged, fireValueCommitted, formatValue, formattedMinimum, locale, maximum, minimum, rawValue, required, roundedRawValue, value]);
  useEffect(function updateA11yInfo() {
    const input = inputRef.current;
    if (!input) return;
    if (isUnexpectedBlankValueRef.current) {
      input.setAttribute('aria-valuetext', 'blank');
      input.removeAttribute('aria-valuenow');
      return;
    }
    if (isNaN(roundedRawValue)) {
      input.setAttribute('aria-valuetext', value?.toString() ?? '');
      input.removeAttribute('aria-valuenow');
    } else {
      input.setAttribute('aria-valuenow', roundedRawValue.toString());
      input.removeAttribute('aria-valuetext');
    }

    // Add an assertion to ensure the minimum and maximum be in round.
    input.setAttribute('aria-valuemin', minimum.toString());
    input.setAttribute('aria-valuemax', maximum.toString());
  }, [value, roundedRawValue, minimum, maximum, editMode, readOnly]);
  const setInputRef = input => {
    inputRef.current = input;
    setRef(ref, input);
  };

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = getNumericStepperDesignTokens(dt, {
    disabled,
    status: valueState.status,
    editable: editMode === 'combo',
    variant: 'standard',
    isCustomFocusTurnedOn: true,
    density,
    isNewTokenValueFlagEnabled
  });
  if (readOnly) {
    return /*#__PURE__*/_jsx(NumericStepperInput, {
      ...props,
      ref: setInputRef,
      readOnly: true,
      disabled: disabled,
      maxLength: maxLength,
      value: getLocaleStringValue(value, locale),
      onStateChange: () => {}
    });
  } else {
    return /*#__PURE__*/_jsx(SpinButtons, {
      testId: testId,
      tooltip: tooltip,
      style: {
        ...tokens,
        ...props.style
      },
      className: className,
      disabled: disabled,
      minimum: minimum,
      maximum: maximum,
      value: isNaN(roundedRawValue) ? undefined : roundedRawValue,
      stepSize: stepSize,
      pageSize: pageSize,
      onValueChange: onSpinButtonsValueChange,
      onValueCommit: onSpinButtonsValueCommit,
      children: /*#__PURE__*/_jsx(NumericStepperInput, {
        ...props,
        disabled: disabled,
        editMode: editMode,
        required: required,
        maxLength: maxLength,
        value: isLiveRef.current || valueState.status || value === '' ? getLocaleStringValue(value, locale) : formatValue,
        testId: postfixTestId(testId, 'inputField'),
        status: valueState.status,
        message: valueState.message,
        ref: setInputRef,
        onStateChange: onInputStateChange
      })
    });
  }
});
function assertNumberFormatOptionsValidity(options) {
  if (options.minimumFractionDigits < MINIMUM_FRACTION_DIGITS) {
    throw new Error(`The minimum number of fraction digits cannot be less than ${MINIMUM_FRACTION_DIGITS}.`);
  }
  if (options.maximumFractionDigits > MAXIMUM_FRACTION_DIGITS) {
    throw new Error(`The maximum number of fraction digits cannot be larger than ${MAXIMUM_FRACTION_DIGITS}.`);
  }
  if (options.maximumFractionDigits < options.minimumFractionDigits) {
    throw new Error(`The maximum number of fraction digits cannot be less than the minimum number of fraction digits.`);
  }
}
function assertMinMaxValidity(min, max, maxFractionDigits) {
  const fractionDigits = Math.max(min.toString().split('.')[1]?.length ?? 0, max.toString().split('.')[1]?.length ?? 0);
  if (fractionDigits > maxFractionDigits) {
    throw new Error(`The number of fraction digits in minimum/maximum cannot be larger than maximum fraction digits.`);
  }
  if (min < MINIMUM_VALUE) {
    throw new Error(`The minimum cannot be less than ${MINIMUM_VALUE}.`);
  }
  if (max > MAXIMUM_VALUE) {
    throw new Error(`The maximum cannot be greater than ${MAXIMUM_VALUE}.`);
  }
  if (max < min) {
    throw new Error(`The maximum cannot be less than minimum.`);
  }
}
function assertStepSizeValidity(stepSize, pageSize, maxFractionDigits) {
  const fractionDigits = Math.max(stepSize.toString().split('.')[1]?.length ?? 0, pageSize.toString().split('.')[1]?.length ?? 0, 0);
  if (fractionDigits > maxFractionDigits) {
    throw new Error(`The number of fraction digits in stepSize/pageSize cannot be larger than maximum fraction digits.`);
  }
  if (stepSize <= 0) {
    throw new Error(`The stepSize cannot be less than or equals 0.`);
  }
  if (pageSize <= 0) {
    throw new Error(`The pageSize cannot be less than or equals 0.`);
  }
  if (pageSize < stepSize) {
    throw new Error(`The pageSize cannot be less than stepSize.`);
  }
}
function validate(value, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, formatter) {
  const validateProps = {
    value: (value ?? '').toLocaleString(locale),
    isRequired,
    allowIntegersOnly: allowIntegerOnly,
    isLive,
    appLocale: locale,
    minValue: minimum,
    maxValue: maximum,
    inclusiveMode: 3,
    formatter
  };
  return isLive ? validateNumericInput(validateProps.value, validateProps.appLocale, validateProps.isRequired, validateProps.allowIntegersOnly, validateProps.isLive) : validateRangeInput(validateProps);
}
function getFinalStatusAndMessage(value, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage, formatter) {
  const localeStringValue = getLocaleStringValue(value, locale);
  const validateState = validate(localeStringValue, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, formatter);

  // consumer status has higher priority
  if (!customizedStatus) {
    return {
      status: validateState.status,
      message: validateState.message
    };
  } else if (customizedStatus === 'alert' || customizedStatus === 'warning' && validateState.status !== 'alert') {
    return {
      status: customizedStatus,
      message: customizedMessage
    };
  } else {
    return {
      status: validateState.status,
      message: validateState.message
    };
  }
}
function getLocaleStringValue(value, locale = 'en-US', options) {
  return value === undefined ? '' : typeof value === 'string' // if value is string, it is assumed locale string already.
  ? value : value.toLocaleString(toLatinNumberingSystemLocale(locale), options);
}
function parseValue(value, locale = 'en-US', options) {
  const {
    rawValue,
    formatValue
  } = parseAndFormatInput(getLocaleStringValue(value, locale, options), locale, options);
  const roundedRawValue = round(rawValue, options.maximumFractionDigits);
  return {
    rawValue,
    formatValue: formatValue.replace(/\u200e/, ''),
    roundedRawValue
  };
}

// TODO This code may be should be put in useInputValidationAdapter
function round(value, fractionDigits) {
  // 0.1235 cannot be rounded to 0.124 by (0.1235).toFix(3) directly
  const times = Math.pow(10, fractionDigits);
  return Number((value * times).toFixed(0)) / times;
}
function getErrorType({
  invalidValue,
  allowIntegerOnly,
  min,
  max,
  locale
}) {
  if (typeof invalidValue === 'string' && (isNaN(Number(invalidValue)) || invalidValue.trim() === '' || !checkValidNumberFormat(invalidValue, locale, false))) {
    return 'notNumber';
  }
  const value = Number(invalidValue);
  if (value > max) {
    return 'greaterThanMax';
  } else if (value < min) {
    return 'lessThanMin';
  } else if (allowIntegerOnly && Math.floor(value) < value) {
    return 'notInteger';
  }
  return undefined;
}
function getValidDecimalValue(value, locale = 'en-US', options) {
  const {
    rawValue
  } = parseAndFormatInput(value, locale, options);
  if (isNaN(rawValue)) {
    return value;
  }
  const [decimalSeparator] = getNumberSeparators(locale);
  const decimalSeparatorPosition = value.indexOf(decimalSeparator);
  return decimalSeparatorPosition > -1 ? value.substring(0, decimalSeparatorPosition) + value.substring(decimalSeparatorPosition, decimalSeparatorPosition + (options?.maximumFractionDigits ?? MAXIMUM_FRACTION_DIGITS) + 1) : value;
}
function isPrintableKey(e) {
  if (e.ctrlKey || e.altKey || e.metaKey) {
    return false;
  }

  // during IME composition, think of it as printable
  if (e.keyCode === 229 || e.key.length === 1) {
    return true;
  }
  return false;
}
function isNumberIrrelevantKey(key, locale) {
  const [decimalSeparator, g] = getNumberSeparators(locale);
  const groupingSeparator = g === '\\s' ? ' ' : g;
  const numberRelatedKeys = `0123456789+-${decimalSeparator}${groupingSeparator}`;
  return numberRelatedKeys.indexOf(key) === -1;
}
function normalize(value, options) {
  return parseFloat(value.toLocaleString('en-US', {
    ...options,
    useGrouping: false
  }));
}
export const getNumericStepperDesignTokens = (dt, opts) => {
  const inputTokens = getInputDesignTokens(dt, opts);
  const buttonTokens = getButtonDesignTokens(dt, {
    variant: ButtonVariant.Icon,
    density: opts?.density
  });
  return {
    ...renderDesignTokens({
      'bd-width': '0 0 1px 0',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'bd-width': dt.tokens['size-border-width-s']
      }),
      'input-bd': opts?.disabled ? inputTokens['--input-disabled-bd'] : inputTokens['--input-bd'],
      'input-bg': inputTokens['--input-bg'],
      'input-focus-bd': opts?.status ? inputTokens['--input-bd'] : dt.tokens['color-action-border-bottom-control-selected'],
      'input-focus-outline': 'var(--focus-light-bg)',
      'input-focus-outline-offset': 'calc(-1 * var(--focus-width))',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'input-focus-outline-offset': 'calc(-2px + var(--focus-width)* -1)'
      }),
      'input-inline-pd': opts?.density === 'high' ? dt.tokens['space-base-xs'] : dt.tokens['space-base-s'],
      'button-icon-width': buttonTokens['--icon-width'],
      'button-inline-padding': opts?.density === 'high' ? buttonTokens['--button-spacing-xs'] : buttonTokens['--button-spacing-s'],
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'numeric-stepper')
  };
};
//# sourceMappingURL=NumericStepper.js.map